import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { UserRole } from "@/types";
import { getUserProfile } from "@/lib/supabase-auth";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userRole: UserRole | null;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Maximum time to wait for auth initialization before giving up
const AUTH_TIMEOUT_MS = 8000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole | null>(null);

  const isProfileFetchingRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);
  const authInitializedRef = useRef(false);
  const authCompletedRef = useRef(false);

  const completeLoading = useCallback(() => {
    if (!authCompletedRef.current) {
      authCompletedRef.current = true;
      setLoading(false);
    }
  }, []);

  const fetchUserRole = useCallback(
    async (userId: string, forceRefetch = false) => {
      if (isProfileFetchingRef.current) {
        console.log("AuthProvider: Already fetching role, skipping");
        return;
      }

      if (!forceRefetch && lastUserIdRef.current === userId) {
        console.log("AuthProvider: Role already fetched for this user");
        completeLoading();
        return;
      }

      isProfileFetchingRef.current = true;
      lastUserIdRef.current = userId;

      const timeoutId = setTimeout(() => {
        console.warn("AuthProvider: Role fetch timeout, completing loading");
        completeLoading();
        isProfileFetchingRef.current = false;
      }, 5000);

      try {
        console.log("AuthProvider: Fetching user role for:", userId);
        const { data } = await getUserProfile(userId);
        console.log("AuthProvider: Role data:", data);
        setUserRole((data?.role as UserRole) || null);
      } catch (error) {
        console.error("AuthProvider: Error fetching role:", error);
        setUserRole(null);
      } finally {
        clearTimeout(timeoutId);
        completeLoading();
        isProfileFetchingRef.current = false;
      }
    },
    [completeLoading]
  );

  useEffect(() => {
    if (authInitializedRef.current) return;
    authInitializedRef.current = true;

    let mounted = true;

    const globalTimeout = setTimeout(() => {
      if (mounted && !authCompletedRef.current) {
        console.warn("AuthProvider: Global auth timeout reached, forcing loading to false");
        completeLoading();
      }
    }, AUTH_TIMEOUT_MS);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;

      console.log("AuthProvider: Auth state change:", {
        event,
        hasSession: !!nextSession,
        hasUser: !!nextSession?.user,
        userId: nextSession?.user?.id,
      });

      if (event === "SIGNED_OUT" || !nextSession) {
        setSession(null);
        setUser(null);
        setUserRole(null);
        lastUserIdRef.current = null;
        isProfileFetchingRef.current = false;
        completeLoading();
        return;
      }

      setSession(nextSession);
      setUser(nextSession.user ?? null);

      if (nextSession.user) {
        fetchUserRole(nextSession.user.id);
      } else {
        completeLoading();
      }
    });

    const checkSession = async () => {
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        if (!mounted) return;

        if (error) {
          console.error("AuthProvider: Error getting session:", error);
          completeLoading();
          return;
        }

        if (initialSession?.user) {
          console.log("AuthProvider: Found existing session, validating...");

          const refreshPromise = supabase.auth.refreshSession();
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("Session refresh timeout")), 5000)
          );

          try {
            const { data: refreshData, error: refreshError } = await Promise.race([
              refreshPromise,
              timeoutPromise,
            ]);

            if (!mounted) return;

            // If refresh fails, decide whether this session is truly invalid.
            if (refreshError || !refreshData.session) {
              const msg = String((refreshError as any)?.message ?? "");
              const code = String((refreshError as any)?.code ?? "");
              const status = (refreshError as any)?.status;
              const looksLikeMissingSession =
                /session_not_found/i.test(msg) || /session_not_found/i.test(code) || status === 403;

              if (looksLikeMissingSession) {
                console.warn(
                  "AuthProvider: Server session missing, clearing local auth and signing out",
                  { msg, code, status }
                );

                try {
                  Object.keys(localStorage)
                    .filter((k) => k.startsWith("sb-"))
                    .forEach((k) => localStorage.removeItem(k));
                } catch {
                  // ignore
                }

                try {
                  await supabase.auth.signOut({ scope: "local" });
                } catch {
                  // ignore
                }

                setSession(null);
                setUser(null);
                setUserRole(null);
                completeLoading();
                return;
              }

              console.warn("AuthProvider: Session refresh failed, using existing session", {
                hasRefreshError: !!refreshError,
                hasRefreshedSession: !!refreshData?.session,
              });
              setSession(initialSession);
              setUser(initialSession.user);
              fetchUserRole(initialSession.user.id);
              return;
            }

            setSession(refreshData.session);
            setUser(refreshData.session.user);
            fetchUserRole(refreshData.session.user.id);
          } catch (refreshErr: any) {
            console.warn("AuthProvider: Session refresh failed or timed out:", refreshErr);

            const msg = typeof refreshErr?.message === "string" ? refreshErr.message : String(refreshErr);
            const looksLikeInvalidRefresh = /invalid refresh token|refresh token not found|refresh_token_not_found/i.test(msg);

            if (looksLikeInvalidRefresh) {
              console.warn("AuthProvider: Detected invalid refresh token, clearing auth storage and signing out");
              try {
                Object.keys(localStorage)
                  .filter((k) => k.startsWith("sb-"))
                  .forEach((k) => localStorage.removeItem(k));
              } catch {
                // ignore
              }

              try {
                await supabase.auth.signOut();
              } catch {
                // ignore
              }

              setSession(null);
              setUser(null);
              setUserRole(null);
              completeLoading();
              return;
            }

            // On non-auth refresh failures, just use the existing session
            setSession(initialSession);
            setUser(initialSession.user);
            fetchUserRole(initialSession.user.id);
          }
        } else {
          completeLoading();
        }
      } catch (err) {
        console.error("AuthProvider: Exception getting session:", err);
        if (mounted) completeLoading();
      }
    };

    checkSession();

    return () => {
      mounted = false;
      clearTimeout(globalTimeout);
      subscription.unsubscribe();
    };
  }, [fetchUserRole, completeLoading]);

  const value: AuthContextValue = {
    user,
    session,
    loading,
    userRole,
    isAuthenticated: !!session,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
